import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Save, AlertCircle, Check, ChevronDown, ChevronRight,
  Calculator, TrendingUp, DollarSign, Package, Users, 
  BarChart3, Factory, Truck, ArrowLeftRight, Landmark, FileText, Plus, Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import forecastApi from '../../api/forecast';
import priceComponentsApi from '../../api/priceComponents';
import apiClient from '../../api/client';
import ScenarioImpactBanner from './ScenarioImpactBanner';

// Constants for time granularity
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTER_MONTHS = { Q1: [1, 2, 3], Q2: [4, 5, 6], Q3: [7, 8, 9], Q4: [10, 11, 12] };

/**
 * UnifiedPlanningGrid Component
 * 
 * A consolidated view combining Volume + Price Breakdown + Revenue calculation
 * 
 * Features:
 * - Volume: IHS Market Volume (read-only), Company Allocation (editable), Take Rate (editable), Volume Plan
 * - Price Breakdown: Base Material + Labor + Overhead + Adjustments = Effective Price
 * - Revenue: Planned Volume × Effective Price
 * - Dynamic columns based on SOP/EOP dates
 * - Inline editing with visual feedback
 * - Collapsible price component breakdown
 * - Supports Annual, Quarterly, and Monthly granularity views
 * 
 * Volume Calculation (NEW):
 * - IHS Market Volume: Auto-populated from linked IHS carline (read-only)
 * - Company Allocation: Your expected volume from RFQ or manual entry (editable)
 * - Take Rate: Editable as (Company Allocation ÷ IHS Market Volume × 100) (read-only)
 * - Volume Plan: Company Allocation + Volume Adjustment
 */
const UnifiedPlanningGrid = ({ 
  partNumber, 
  scenarioName = 'Active',
  programId,
  contextRequired = false,
  contextLabel,
  readOnly = false,
  readOnlyReason,
  sopDate,
  eopDate,
  onDataChange,
  onPriceComponentChange
}) => {
  // Data state
  const [forecasts, setForecasts] = useState([]);
  const [priceComponents, setPriceComponents] = useState([]);
  const [priceSchedule, setPriceSchedule] = useState({ tiers: [] }); // Price schedule for LTA
  const [partPricing, setPartPricing] = useState({ quoted_price: null, awarded_price: null }); // Part pricing
  const [partId, setPartId] = useState(null); // Part UUID for price schedule API calls
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMessage, setSavedMessage] = useState('');
  // Part Master (no program context) state — identity/where-used, not an editable plan
  const [contextMode, setContextMode] = useState(null);
  const [whereUsed, setWhereUsed] = useState([]);
  
  // Edit state for quoted price (bidirectional with price schedule)
  const [editedQuotedPrices, setEditedQuotedPrices] = useState({}); // year -> price in dollars
  
  // Refs for callback functions to avoid stale closures in useEffect
  const onDataChangeRef = useRef(onDataChange);
  
  // View state
  const [granularity, setGranularity] = useState('annual'); // 'annual', 'quarterly', 'monthly'
  const [expandedSections, setExpandedSections] = useState({
    volume: true,
    price: true,
    priceBreakdown: false, // Price component breakdown (collapsed by default)
    revenue: true
  });
  
  // Edit state
  const [editedCells, setEditedCells] = useState({});
  const [editedPriceComponents, setEditedPriceComponents] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Price component type configurations
  const priceComponentTypes = useMemo(() => ({
    base_material: { label: 'Base Material', icon: Package, color: 'text-blue-600', bgColor: 'bg-blue-50', isBase: true },
    labor_index: { label: 'Labor Cost', icon: Users, color: 'text-purple-600', bgColor: 'bg-purple-50', isBase: true },
    material_index: { label: 'Material Index', icon: BarChart3, color: 'text-indigo-600', bgColor: 'bg-indigo-50', isBase: true },
    overhead: { label: 'Overhead', icon: Factory, color: 'text-gray-600', bgColor: 'bg-gray-50', isBase: true },
    logistics: { label: 'Logistics', icon: Truck, color: 'text-green-600', bgColor: 'bg-green-50', isBase: false },
    fx_impact: { label: 'FX Impact', icon: ArrowLeftRight, color: 'text-yellow-600', bgColor: 'bg-yellow-50', isBase: false },
    tariff: { label: 'Tariff', icon: Landmark, color: 'text-red-600', bgColor: 'bg-red-50', isBase: false },
    surcharge: { label: 'Surcharges', icon: TrendingUp, color: 'text-orange-600', bgColor: 'bg-orange-50', isBase: false },
    other: { label: 'Other', icon: FileText, color: 'text-slate-600', bgColor: 'bg-slate-50', isBase: false },
  }), []);

  // Load forecast data and price components
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // console.log('UnifiedPlanningGrid: Loading data for', partNumber, scenarioName);
      
      // Load forecast data, price components, and part pricing in parallel
      const [forecastData, priceData] = await Promise.all([
        forecastApi.getForecastData(partNumber, scenarioName, 'all', { programId }),
        priceComponentsApi.getComponents(partNumber, scenarioName).catch(() => ({ components: [] }))
      ]);
      
      // Also fetch part details for quoted/awarded prices
      try {
        const partResponse = await apiClient.get(`/api/v1/parts/${partNumber}`, {
          params: programId ? { program_id: programId } : {}
        });
        if (partResponse.data?.success && partResponse.data?.data) {
          const part = partResponse.data.data;
          setPartId(part.id); // Store part UUID for price schedule updates
          setPartPricing({
            quoted_price: part.quoted_price_cents ? part.quoted_price_cents / 100 : null,
            awarded_price: part.awarded_price_cents ? part.awarded_price_cents / 100 : null,
            target_price: part.target_price_cents ? part.target_price_cents / 100 : null
          });
          
          // Fetch price schedule if part has UUID
          if (part.id) {
            try {
              const scheduleResponse = await apiClient.get(`/api/v1/parts/${part.id}/price-schedule`);
              if (scheduleResponse.data?.success) {
                setPriceSchedule(scheduleResponse.data.data);
              }
            } catch (schedErr) {
              // console.log('Could not fetch price schedule:', schedErr);
            }
          }
        }
      } catch (pricingErr) {
        // console.log('Could not fetch part pricing:', pricingErr);
      }
      
      // console.log('UnifiedPlanningGrid: Received forecast data', forecastData);
      // console.log('UnifiedPlanningGrid: Received price components', priceData);
      
      setForecasts(forecastData.forecasts || []);
      setContextMode(forecastData.context_mode || null);
      setWhereUsed(forecastData.where_used || []);
      setPriceComponents(priceData.components || []);
    } catch (err) {
      console.error('UnifiedPlanningGrid: Error loading data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [partNumber, scenarioName, programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Keep callback ref updated
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);
  
  // Notify parent of data changes separately to avoid infinite loops
  useEffect(() => {
    if (onDataChangeRef.current && forecasts.length > 0) {
      onDataChangeRef.current(forecasts);
    }
  }, [forecasts]);

  // Determine IHS range from SOP/EOP dates
  const ihsRange = useMemo(() => {
    const sopYear = sopDate ? new Date(sopDate).getFullYear() : null;
    const eopYear = eopDate ? new Date(eopDate).getFullYear() : null;
    const sopMonth = sopDate ? new Date(sopDate).getMonth() + 1 : 1; // 1-indexed
    const eopMonth = eopDate ? new Date(eopDate).getMonth() + 1 : 12;
    return { sopYear, eopYear, sopMonth, eopMonth };
  }, [sopDate, eopDate]);

  // Determine active years based on Option A: Union of user data years + IHS range
  // This ensures user data is NEVER hidden even if IHS dates shift
  const activeYears = useMemo(() => {
    // Collect years from forecasts and price components (user data)
    const forecastYears = forecasts.map(f => f.year);
    const priceYears = priceComponents.map(c => c.year);
    const userDataYears = new Set([...forecastYears, ...priceYears]);
    
    // Get IHS range years
    const ihsYears = new Set();
    if (ihsRange.sopYear && ihsRange.eopYear) {
      for (let y = ihsRange.sopYear; y <= ihsRange.eopYear; y++) {
        ihsYears.add(y);
      }
    }
    
    // Union of user data years + IHS range years
    const allYears = [...new Set([...userDataYears, ...ihsYears])].sort((a, b) => a - b);
    
    if (allYears.length === 0) {
      // Default to current year + 5 years if no data
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, i) => currentYear + i);
    }
    
    return allYears;
  }, [forecasts, priceComponents, ihsRange]);

  // Helper to check if a year/month is within IHS range
  const isWithinIhsRange = useCallback((year, month = null) => {
    if (!ihsRange.sopYear || !ihsRange.eopYear) return true; // No IHS data = all in range
    
    if (year < ihsRange.sopYear || year > ihsRange.eopYear) return false;
    
    // Check month boundary for SOP/EOP years
    if (month !== null) {
      if (year === ihsRange.sopYear && month < ihsRange.sopMonth) return false;
      if (year === ihsRange.eopYear && month > ihsRange.eopMonth) return false;
    }
    
    return true;
  }, [ihsRange]);

  // Month names and quarter definitions are now constants at the top of the file

  // Generate time columns based on granularity
  const timeColumns = useMemo(() => {
    if (granularity === 'annual') {
      return activeYears.map(year => ({
        key: `${year}`,
        year,
        label: year.toString(),
        isOutOfRange: !isWithinIhsRange(year)
      }));
    } else if (granularity === 'quarterly') {
      const columns = [];
      activeYears.forEach(year => {
        ['Q1', 'Q2', 'Q3', 'Q4'].forEach((q, qIdx) => {
          // Quarter is out of range if all its months are out of range
          const qMonths = QUARTER_MONTHS[q];
          const allOutOfRange = qMonths.every(m => !isWithinIhsRange(year, m));
          columns.push({
            key: `${year}-${q}`,
            year,
            quarter: q,
            quarterNum: qIdx + 1,
            label: `${q} '${year.toString().slice(-2)}`,
            isOutOfRange: allOutOfRange
          });
        });
      });
      return columns;
    } else { // monthly
      const columns = [];
      activeYears.forEach(year => {
        for (let m = 1; m <= 12; m++) {
          columns.push({
            key: `${year}-${m}`,
            year,
            month: m,
            label: `${MONTH_NAMES[m - 1]} '${year.toString().slice(-2)}`,
            isOutOfRange: !isWithinIhsRange(year, m)
          });
        }
      });
      return columns;
    }
  }, [activeYears, granularity, isWithinIhsRange]);

  // Transform price components into grid structure: { [year]: { [componentType]: amount } }
  const priceComponentsByYear = useMemo(() => {
    const byYear = {};
    
    activeYears.forEach(year => {
      byYear[year] = {};
      Object.keys(priceComponentTypes).forEach(type => {
        byYear[year][type] = 0;
      });
    });
    
    priceComponents.forEach(comp => {
      if (byYear[comp.year] && comp.component_type) {
        byYear[comp.year][comp.component_type] = (byYear[comp.year][comp.component_type] || 0) + (comp.amount || 0);
      }
    });
    
    return byYear;
  }, [priceComponents, activeYears, priceComponentTypes]);

  // Calculate price component totals for a year
  const getPriceComponentTotals = useCallback((year) => {
    const yearComponents = priceComponentsByYear[year] || {};
    let baseTotal = 0;
    let adjustmentTotal = 0;
    
    Object.entries(priceComponentTypes).forEach(([type, config]) => {
      const key = `pc-${type}-${year}`;
      const value = editedPriceComponents[key] !== undefined 
        ? editedPriceComponents[key] 
        : (yearComponents[type] || 0);
      
      if (config.isBase) {
        baseTotal += value;
      } else {
        adjustmentTotal += value;
      }
    });
    
    return { base: baseTotal, adjustments: adjustmentTotal, total: baseTotal + adjustmentTotal };
  }, [priceComponentsByYear, priceComponentTypes, editedPriceComponents]);

  // Get price component value (edited or original)
  const getPriceComponentValue = (type, year) => {
    const key = `pc-${type}-${year}`;
    if (editedPriceComponents[key] !== undefined) {
      return editedPriceComponents[key];
    }
    return priceComponentsByYear[year]?.[type] || 0;
  };

  // Check if price component was edited
  const isPriceComponentEdited = (type, year) => {
    const key = `pc-${type}-${year}`;
    return editedPriceComponents[key] !== undefined;
  };

  // Aggregate data by year - uses S&OP fields from API
  const yearlyData = useMemo(() => {
    const byYear = {};
    
    activeYears.forEach(year => {
      const yearForecasts = forecasts.filter(f => f.year === year);
      const monthlyForecasts = yearForecasts.filter(f => f.month);
      const annualForecasts = yearForecasts.filter(f => !f.month);
      const planningRows = annualForecasts.length > 0 ? annualForecasts : monthlyForecasts;
      
      // Aggregate volume values (sum across months)
      const volumePlan = planningRows.reduce((sum, f) => sum + (f.volume_plan || 0), 0);
      const volumeActual = planningRows.reduce((sum, f) => sum + (f.volume_actual || 0), 0);
      // Server-canonical revenue (from commercial calculator) - displayed as-is in read mode
      const revenueCents = planningRows.reduce((sum, f) => sum + (f.revenue_cents || 0), 0);
      
      // NEW: Aggregate IHS Market Volume (from linked carlines)
      const ihsMarketVolumeTotal = planningRows.reduce((sum, f) => sum + (f.ihs_market_volume || 0), 0);
      
      // NEW: Aggregate Company Allocation (from RFQ or manual entry)
      const companyAllocationTotal = planningRows.reduce((sum, f) => sum + (f.company_allocation || f.ihs_base || 0), 0);
      
      // Volume Adjustment total
      const volumeAdjTotal = planningRows.reduce((sum, f) => sum + (f.volume_adjustment || 0), 0);

      // Driver of record for the year: 'manual' (allocation override) vs 'derived' (from take rate).
      // Preserved on price/PPV-only edits so we don't accidentally flip the driver.
      const yearAllocationSource = (planningRows.find((f) => f.allocation_source) || {}).allocation_source || 'derived';

      const explicitTakeRates = planningRows
        .map((f) => f.take_rate)
        .filter((value) => value !== null && value !== undefined);
      const calculatedTakeRate = explicitTakeRates.length > 0
        ? Math.round((explicitTakeRates.reduce((sum, value) => sum + Number(value || 0), 0) / explicitTakeRates.length) * 100) / 100
        : ihsMarketVolumeTotal > 0
          ? Math.round((companyAllocationTotal / ihsMarketVolumeTotal) * 100 * 100) / 100
          : null;
      
      // PPV average (same across months typically)
      const avgPpv = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.ppv || 1), 0) / planningRows.length
        : 1;
      
      // Price fields - use average (prices are typically same across months in a year)
      const avgBasePrice = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.base_price || 0), 0) / planningRows.length
        : 0;
      const avgLtaPercent = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.lta_percent || 0), 0) / planningRows.length
        : 0;
      const avgPriceAdj = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.price_adjustment || 0), 0) / planningRows.length
        : 0;
      const avgPricePlan = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.price_plan || 0), 0) / planningRows.length
        : 0;
      const avgPriceActual = planningRows.length > 0
        ? planningRows.reduce((sum, f) => sum + (f.price_actual || 0), 0) / planningRows.length
        : 0;
      
      byYear[year] = {
        year,
        // NEW: IHS Market Volume (from linked carlines - read only)
        ihs_market_volume: ihsMarketVolumeTotal,
        // NEW: Company Allocation (from RFQ/manual - editable)
        company_allocation: companyAllocationTotal,
        // DEPRECATED: Keep for backward compatibility
        ihs_base: companyAllocationTotal || volumePlan,
        // Take Rate
        take_rate: calculatedTakeRate,
        ppv: avgPpv, // Parts per vehicle
        volume_adjustment: volumeAdjTotal,
        allocation_source: yearAllocationSource,
        
        // Calculated/Actual Volume
        volume_plan: volumePlan,
        volume_actual: volumeActual,
        // Server-canonical revenue (cents)
        revenue_cents: revenueCents,

        // S&OP Pricing Fields (averages - same per unit across months)
        base_price: avgBasePrice || avgPricePlan, // Fallback to price_plan
        lta_percent: avgLtaPercent, // LTA reduction %
        price_adjustment: avgPriceAdj,
        price_plan: avgPricePlan,
        price_actual: avgPriceActual,
        
        // Months data for drill-down
        months: monthlyForecasts
      };
    });
    
    return byYear;
  }, [forecasts, activeYears]);

  // Aggregate data by month - raw monthly data from API
  const monthlyData = useMemo(() => {
    const byMonth = {};
    
    forecasts.forEach(f => {
      if (f.month) { // Only monthly records
        const key = `${f.year}-${f.month}`;
        const ihsMarket = f.ihs_market_volume || 0;
        const allocation = f.company_allocation || f.ihs_base || 0;
        const calculatedTakeRate = f.take_rate !== null && f.take_rate !== undefined
          ? Number(f.take_rate)
          : ihsMarket > 0
            ? Math.round((allocation / ihsMarket) * 100 * 100) / 100
            : null;
        
        byMonth[key] = {
          year: f.year,
          month: f.month,
          // NEW: IHS Market Volume (read-only)
          ihs_market_volume: ihsMarket,
          // NEW: Company Allocation (editable)
          company_allocation: allocation,
          allocation_source: f.allocation_source || 'manual',
          // DEPRECATED: Keep for backward compatibility
          ihs_base: allocation || f.volume_plan || 0,
          // Take Rate (editable)
          take_rate: calculatedTakeRate,
          ppv: f.ppv || 1,
          volume_adjustment: f.volume_adjustment || 0,
          // Calculated/Actual Volume
          volume_plan: f.volume_plan || 0,
          volume_actual: f.volume_actual || 0,
          revenue_cents: f.revenue_cents || 0,
          // S&OP Pricing Fields
          base_price: f.base_price || f.price_plan || 0,
          lta_percent: f.lta_percent || 0,
          price_adjustment: f.price_adjustment || 0,
          price_plan: f.price_plan || 0,
          price_actual: f.price_actual || null
        };
      }
    });
    
    // Fill in empty months for years that have data
    activeYears.forEach(year => {
      for (let m = 1; m <= 12; m++) {
        const key = `${year}-${m}`;
        if (!byMonth[key]) {
          byMonth[key] = {
            year,
            month: m,
            ihs_market_volume: 0,
            company_allocation: 0,
            allocation_source: 'manual',
            ihs_base: 0,
            take_rate: null,
            ppv: 1,
            volume_adjustment: 0,
            volume_plan: 0,
            volume_actual: 0,
            revenue_cents: 0,
            base_price: 0,
            lta_percent: 0,
            price_adjustment: 0,
            price_plan: 0,
            price_actual: null
          };
        }
      }
    });
    
    return byMonth;
  }, [forecasts, activeYears]);

  // Aggregate data by quarter
  const quarterlyData = useMemo(() => {
    const byQuarter = {};
    
    activeYears.forEach(year => {
      Object.entries(QUARTER_MONTHS).forEach(([q, months]) => {
        const key = `${year}-${q}`;
        const quarterForecasts = forecasts.filter(f => f.year === year && months.includes(f.month));
        
        // Aggregate volume values (sum across quarter months)
        const volumePlan = quarterForecasts.reduce((sum, f) => sum + (f.volume_plan || 0), 0);
        const volumeActual = quarterForecasts.reduce((sum, f) => sum + (f.volume_actual || 0), 0);
        const revenueCents = quarterForecasts.reduce((sum, f) => sum + (f.revenue_cents || 0), 0);
        
        // NEW: Aggregate IHS Market Volume
        const ihsMarketVolumeTotal = quarterForecasts.reduce((sum, f) => sum + (f.ihs_market_volume || 0), 0);
        // NEW: Aggregate Company Allocation
        const companyAllocationTotal = quarterForecasts.reduce((sum, f) => sum + (f.company_allocation || f.ihs_base || 0), 0);
        const volumeAdjTotal = quarterForecasts.reduce((sum, f) => sum + (f.volume_adjustment || 0), 0);
        
        const explicitTakeRates = quarterForecasts
          .map((f) => f.take_rate)
          .filter((value) => value !== null && value !== undefined);
        const calculatedTakeRate = explicitTakeRates.length > 0
          ? Math.round((explicitTakeRates.reduce((sum, value) => sum + Number(value || 0), 0) / explicitTakeRates.length) * 100) / 100
          : ihsMarketVolumeTotal > 0
            ? Math.round((companyAllocationTotal / ihsMarketVolumeTotal) * 100 * 100) / 100
            : null;
        
        // PPV average
        const avgPpv = quarterForecasts.length > 0
          ? quarterForecasts.reduce((sum, f) => sum + (f.ppv || 1), 0) / quarterForecasts.length
          : 1;
        
        // Price fields - use average
        const avgBasePrice = quarterForecasts.length > 0 
          ? quarterForecasts.reduce((sum, f) => sum + (f.base_price || 0), 0) / quarterForecasts.length
          : 0;
        const avgLtaPercent = quarterForecasts.length > 0
          ? quarterForecasts.reduce((sum, f) => sum + (f.lta_percent || 0), 0) / quarterForecasts.length
          : 0;
        const avgPriceAdj = quarterForecasts.length > 0
          ? quarterForecasts.reduce((sum, f) => sum + (f.price_adjustment || 0), 0) / quarterForecasts.length
          : 0;
        const avgPricePlan = quarterForecasts.length > 0 
          ? quarterForecasts.reduce((sum, f) => sum + (f.price_plan || 0), 0) / quarterForecasts.length
          : 0;
        const avgPriceActual = quarterForecasts.length > 0
          ? quarterForecasts.reduce((sum, f) => sum + (f.price_actual || 0), 0) / quarterForecasts.length
          : null;
        
        byQuarter[key] = {
          year,
          quarter: q,
          // NEW: IHS Market Volume (read-only)
          ihs_market_volume: ihsMarketVolumeTotal,
          // NEW: Company Allocation (editable)
          company_allocation: companyAllocationTotal,
          // DEPRECATED: Keep for backward compatibility
          ihs_base: companyAllocationTotal || volumePlan,
          // Take Rate (editable)
          take_rate: calculatedTakeRate,
          ppv: avgPpv,
          volume_adjustment: volumeAdjTotal,
          // Calculated/Actual Volume
          volume_plan: volumePlan,
          volume_actual: volumeActual,
          // Server-canonical revenue (cents)
          revenue_cents: revenueCents,
          // S&OP Pricing Fields
          base_price: avgBasePrice || avgPricePlan,
          lta_percent: avgLtaPercent,
          price_adjustment: avgPriceAdj,
          price_plan: avgPricePlan,
          price_actual: avgPriceActual,
          // Months for this quarter
          months: quarterForecasts
        };
      });
    });
    
    return byQuarter;
  }, [forecasts, activeYears]);

  // Get data for a specific time column based on granularity
  const getColumnData = useCallback((column) => {
    if (granularity === 'annual') {
      return yearlyData[column.year] || {};
    } else if (granularity === 'quarterly') {
      return quarterlyData[column.key] || {};
    } else {
      return monthlyData[column.key] || {};
    }
  }, [granularity, yearlyData, quarterlyData, monthlyData]);

  // Calculate derived values - matches the canonical commercial calculator.
  // Contract: volume_plan = ihs_market_volume * take_rate * content_per_vehicle + adjustment.
  // (company_allocation already equals market * take_rate on the server, so this also
  //  equals company_allocation * ppv + adjustment.) take_rate arrives as a percent.
  // NOTE: used for instant feedback while editing only; read mode displays the
  // server-canonical volume_plan directly, and the grid re-syncs to the server on save.
  const calculatePlannedVolume = (data) => {
    const market = data.ihs_market_volume || 0;
    const takeRatePct = data.take_rate ?? 100;
    const ppv = data.ppv || 1;
    const adjustment = data.volume_adjustment || 0;
    if (market > 0) {
      return Math.max(0, Math.round(market * (takeRatePct / 100) * ppv) + adjustment);
    }
    // Fallback when no IHS market volume is linked: company allocation is already
    // share-adjusted, so do not apply take_rate again.
    const allocation = data.company_allocation || data.ihs_base || 0;
    return Math.max(0, Math.round(allocation * ppv) + adjustment);
  };

  const calculateEffectivePrice = (data) => {
    const basePrice = data.base_price || 0;
    const ltaReduction = basePrice * ((data.lta_percent || 0) / 100);
    const adjustment = data.price_adjustment || 0;
    return basePrice - ltaReduction + adjustment;
  };

  const calculateRevenue = (data) => {
    const volume = data.volume_plan || calculatePlannedVolume(data);
    const price = calculateEffectivePrice(data);
    return volume * price;
  };

  // Get price from schedule for a given year (mid-year lookup)
  const getSchedulePriceForYear = useCallback((year) => {
    if (!priceSchedule?.tiers?.length) return null;
    
    // Use mid-year date for lookup (July 1st)
    const midYearDate = new Date(year, 6, 1); // Month is 0-indexed
    
    for (const tier of priceSchedule.tiers) {
      const startDate = new Date(tier.effective_start_date);
      const endDate = tier.effective_end_date ? new Date(tier.effective_end_date) : null;
      
      if (midYearDate >= startDate && (!endDate || midYearDate <= endDate)) {
        return tier.price_cents / 100; // Convert cents to dollars
      }
    }
    return null;
  }, [priceSchedule]);

  // Handle cell edit - now takes column object instead of just year
  const handleCellClick = (column, field, value, isPriceComponent = false) => {
    if (readOnly) return;
    setEditingCell({ column, field, isPriceComponent });
    setEditValue(value?.toString() || '');
  };

  const handleCellChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;
    
    const { column, field, isPriceComponent } = editingCell;
    
    if (isPriceComponent) {
      // Price component edit - still uses year only
      const key = `pc-${field}-${column.year}`;
      setEditedPriceComponents(prev => ({
        ...prev,
        [key]: parseFloat(editValue) || 0
      }));
    } else {
      // Forecast/S&OP field edit - uses column key for granularity support
      const key = `${column.key}-${field}`;
      setEditedCells(prev => {
        const next = { ...prev, [key]: parseFloat(editValue) || 0 };
        // Take rate and company allocation drive each other - keep only the one just edited.
        if (field === 'take_rate') delete next[`${column.key}-company_allocation`];
        if (field === 'company_allocation') delete next[`${column.key}-take_rate`];
        return next;
      });
    }

    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Get cell value (edited or original) - now uses column key
  const getCellValue = (column, field, originalValue) => {
    const key = `${column.key}-${field}`;
    return editedCells[key] !== undefined ? editedCells[key] : originalValue;
  };

  // Check if cell was edited
  const isCellEdited = (column, field) => {
    const key = `${column.key}-${field}`;
    return editedCells[key] !== undefined;
  };

  // In a program context you plan per-program: take rate, company allocation (bidirectional),
  // PPV, volume adjustments, LTA, and scenario price adjustments are editable. Market volume
  // stays read-only because it comes from the linked IHS/S&P forecast basis.
  const contextDerived = Boolean(programId);
  const isFieldEditable = (field) => {
    if (readOnly) return false;
    if (contextDerived) return ['company_allocation', 'take_rate', 'ppv', 'volume_adjustment', 'lta_percent', 'price_adjustment'].includes(field);
    return true;
  };

  // Resolve the Volume Plan to DISPLAY for a column.
  // - Read mode, or an edit-mode cell whose inputs are untouched: show the
  //   server-canonical volume_plan (ties out to Program 360 / prices; never drifts
  //   from whatever the backend seeded).
  // - An edit-mode cell with edited inputs: recompute for instant feedback, matching
  //   the calculator's semantics (a manual allocation override wins; otherwise
  //   allocation = market x take_rate). The grid re-syncs to the server on save.
  const getDisplayedPlannedVolume = (column, data) => {
    if (readOnly) return data.volume_plan || 0;
    const allocEdited = isCellEdited(column, 'company_allocation');
    const takeEdited = isCellEdited(column, 'take_rate');
    const ppvEdited = isCellEdited(column, 'ppv');
    const adjEdited = isCellEdited(column, 'volume_adjustment');
    if (!allocEdited && !takeEdited && !ppvEdited && !adjEdited) {
      return data.volume_plan || 0;
    }
    const market = data.ihs_market_volume || 0;
    const ppv = getCellValue(column, 'ppv', data.ppv) || 1;
    const adjustment = getCellValue(column, 'volume_adjustment', data.volume_adjustment) || 0;
    const takeRatePct = getCellValue(column, 'take_rate', data.take_rate) ?? 100;
    const allocation = allocEdited
      ? (getCellValue(column, 'company_allocation', data.company_allocation || data.ihs_base) || 0)
      : (market > 0 ? market * (takeRatePct / 100) : (data.company_allocation || data.ihs_base || 0));
    return Math.max(0, Math.round(allocation * ppv) + adjustment);
  };

  // Take rate and company allocation are two views of the same number. Editing one shows the
  // derived other live (handleCellBlur clears the opposite edit so the driver stays unambiguous).
  const getDisplayedTakeRate = (column, data) => {
    if (isCellEdited(column, 'company_allocation')) {
      const market = data.ihs_market_volume || 0;
      const alloc = getCellValue(column, 'company_allocation', data.company_allocation || data.ihs_base) || 0;
      return market > 0 ? Math.round((alloc / market) * 1000) / 10 : 0;
    }
    return data.take_rate;
  };
  const getDisplayedAllocation = (column, data) => {
    if (isCellEdited(column, 'take_rate')) {
      const market = data.ihs_market_volume || 0;
      const takeRatePct = getCellValue(column, 'take_rate', data.take_rate) ?? 0;
      return Math.round(market * (takeRatePct / 100));
    }
    return data.company_allocation || data.ihs_base;
  };

  // Handle quoted price cell click for editing
  const handleQuotedPriceClick = (column, currentPrice) => {
    if (readOnly) return;
    setEditingCell({ column, field: 'quoted_price', isQuotedPrice: true });
    setEditValue(currentPrice?.toString() || '');
  };

  // Handle quoted price blur (save to editedQuotedPrices)
  const handleQuotedPriceBlur = () => {
    if (!editingCell?.isQuotedPrice) return;
    
    const year = editingCell.column.year;
    const newPrice = parseFloat(editValue) || 0;
    
    setEditedQuotedPrices(prev => ({
      ...prev,
      [year]: newPrice
    }));
    
    setEditingCell(null);
  };

  // Get the displayed quoted price for a year (from edited, schedule, or part default)
  const getQuotedPriceForYear = (year) => {
    // First check edited values
    if (editedQuotedPrices[year] !== undefined) {
      return editedQuotedPrices[year];
    }
    
    // Then check price schedule for that year
    const schedulePrice = getSchedulePriceForYear(year);
    if (schedulePrice !== null) {
      return schedulePrice;
    }
    
    // Fall back to part's quoted price
    return partPricing.quoted_price;
  };

  // Check if quoted price for year was edited
  const isQuotedPriceEdited = (year) => {
    return editedQuotedPrices[year] !== undefined;
  };

  // Render editable quoted price cell
  const renderQuotedPriceCell = (column) => {
    const year = column.year;
    const isEditing = editingCell?.isQuotedPrice && editingCell?.column?.year === year;
    const isEdited = isQuotedPriceEdited(year);
    const displayPrice = getQuotedPriceForYear(year);
    
    if (isEditing) {
      return (
        <Input
          type="number"
          step="0.01"
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleQuotedPriceBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleQuotedPriceBlur();
            else if (e.key === 'Escape') setEditingCell(null);
          }}
          className="w-24 h-8 text-right text-sm"
          autoFocus
        />
      );
    }
    
    return (
      <div
        onClick={() => handleQuotedPriceClick(column, displayPrice)}
        className={`${readOnly ? '' : 'cursor-pointer hover:bg-blue-100'} px-2 py-1 rounded text-right text-sm transition-colors relative ${
          isEdited ? 'bg-blue-100 border border-blue-400' : ''
        }`}
      >
        <span className="text-blue-600 font-medium">
          {displayPrice ? `$${displayPrice.toFixed(2)}` : '-'}
        </span>
        {isEdited && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
        )}
      </div>
    );
  };

  // Count total unsaved changes
  const unsavedCount = Object.keys(editedCells).length + Object.keys(editedPriceComponents).length + Object.keys(editedQuotedPrices).length;

  // Save all changes (forecast data and price components)
  const handleSaveAll = async () => {
    if (unsavedCount === 0) return;
    
    setSaving(true);
    try {
      const savePromises = [];
      
      // Save forecast/S&OP changes
      if (Object.keys(editedCells).length > 0) {
        // Group edits by year (for annual) or year+month (for monthly)
        // For quarterly, we update all 3 months of the quarter
        const editsByYear = {};
        const editsByMonth = {};
        
        for (const [key, value] of Object.entries(editedCells)) {
          // Key format depends on granularity:
          // Annual: "2026-ihs_base"
          // Quarterly: "2026-Q1-ihs_base"
          // Monthly: "2026-3-ihs_base"
          const parts = key.split('-');
          const year = parseInt(parts[0]);
          
          if (parts.length === 2) {
            // Annual format: year-field
            const field = parts[1];
            const fieldKey = field === 'company_allocation' ? 'ihs_base' : field;
            if (!editsByYear[year]) {
              const currentData = yearlyData[year] || {};
              editsByYear[year] = {
                ihs_base: currentData.ihs_base || 0,
                take_rate: currentData.take_rate || 100,
                ppv: currentData.ppv || 1,
                volume_adjustment: currentData.volume_adjustment || 0,
                base_price: currentData.base_price || 0,
                lta_percent: currentData.lta_percent || 0,
                price_adjustment: currentData.price_adjustment || 0,
                // Preserve the existing driver for price/PPV-only edits; overridden below if a
                // take-rate or allocation cell was edited.
                allocation_source: currentData.allocation_source || 'derived',
              };
            }
            editsByYear[year][fieldKey] = value;
            // The edited cell decides the driver the backend uses (see codex-brief-context-editing).
            if (field === 'take_rate') editsByYear[year].allocation_source = 'derived';
            if (field === 'company_allocation') editsByYear[year].allocation_source = 'manual';
          } else if (parts.length === 3) {
            // Quarterly (Q1-Q4) or Monthly (1-12)
            const periodOrMonth = parts[1];
            const field = parts[2];
            const fieldKey = field === 'company_allocation' ? 'ihs_base' : field;
            
            if (periodOrMonth.startsWith('Q')) {
              // Quarterly: update all months in the quarter
              const months = QUARTER_MONTHS[periodOrMonth];
              months.forEach(month => {
                const monthKey = `${year}-${month}`;
                if (!editsByMonth[monthKey]) {
                  const currentData = monthlyData[monthKey] || {};
                  editsByMonth[monthKey] = {
                    year,
                    month,
                    ihs_base: currentData.ihs_base || 0,
                    take_rate: currentData.take_rate || 100,
                    ppv: currentData.ppv || 1,
                    volume_adjustment: currentData.volume_adjustment || 0,
                    base_price: currentData.base_price || 0,
                    lta_percent: currentData.lta_percent || 0,
                    price_adjustment: currentData.price_adjustment || 0,
                  };
                }
                // Divide volume-based fields by 3 for quarterly -> monthly
                if (['ihs_base', 'volume_adjustment'].includes(fieldKey)) {
                  editsByMonth[monthKey][fieldKey] = Math.round(value / 3);
                } else {
                  editsByMonth[monthKey][fieldKey] = value;
                }
              });
            } else {
              // Monthly
              const month = parseInt(periodOrMonth);
              const monthKey = `${year}-${month}`;
              if (!editsByMonth[monthKey]) {
                const currentData = monthlyData[monthKey] || {};
                editsByMonth[monthKey] = {
                  year,
                  month,
                  ihs_base: currentData.ihs_base || 0,
                  take_rate: currentData.take_rate || 100,
                  ppv: currentData.ppv || 1,
                  volume_adjustment: currentData.volume_adjustment || 0,
                  base_price: currentData.base_price || 0,
                  lta_percent: currentData.lta_percent || 0,
                  price_adjustment: currentData.price_adjustment || 0,
                };
              }
              editsByMonth[monthKey][fieldKey] = value;
            }
          }
        }
        
        // Save yearly data using S&OP planning endpoint
        Object.entries(editsByYear).forEach(([year, data]) => {
          savePromises.push(
            forecastApi.updateSopPlanning(partNumber, parseInt(year), data, scenarioName, { programId })
          );
        });
        
        // Save monthly data - use individual updates since each month may have different values
        if (Object.keys(editsByMonth).length > 0) {
          const monthlyUpdates = Object.values(editsByMonth);
          // Save each month individually to preserve different values per month
          monthlyUpdates.forEach(data => {
            savePromises.push(
              forecastApi.updateSopPlanning(partNumber, data.year, data, scenarioName, { programId })
            );
          });
        }
      }
      
      // Save price component changes
      if (Object.keys(editedPriceComponents).length > 0) {
        const priceComponentUpdates = [];
        
        for (const [key, value] of Object.entries(editedPriceComponents)) {
          // key format: "pc-{componentType}-{year}"
          const parts = key.split('-');
          const componentType = parts[1];
          const year = parseInt(parts[2]);
          
          priceComponentUpdates.push({
            year,
            month: null,
            component_type: componentType,
            amount: value
          });
        }
        
        if (priceComponentUpdates.length > 0) {
          savePromises.push(
            priceComponentsApi.bulkUpdate(partNumber, priceComponentUpdates, scenarioName, true)
          );
        }
      }
      
      // Save quoted price changes (creates/updates price schedule tiers only)
      if (Object.keys(editedQuotedPrices).length > 0 && partId) {
        for (const [yearStr, priceDollars] of Object.entries(editedQuotedPrices)) {
          const year = parseInt(yearStr);
          const priceCents = Math.round(priceDollars * 100);
          
          // Create or update price schedule tier for this year
          const startDate = `${year}-01-01`;
          const endDate = `${year}-12-31`;
          
          // Check if a tier already covers this year. A tier can start before
          // the edited year, so looking only at the start year creates overlaps.
          const existingTier = priceSchedule?.tiers?.find(t => {
            const start = new Date(t.effective_start_date);
            const end = t.effective_end_date ? new Date(t.effective_end_date) : null;
            const yearStart = new Date(`${year}-01-01T00:00:00`);
            const yearEnd = new Date(`${year}-12-31T23:59:59`);
            return start <= yearEnd && (!end || end >= yearStart);
          });
          
          if (existingTier) {
            // Update existing tier
            savePromises.push(
              apiClient.put(`/api/v1/parts/${partId}/price-schedule/${existingTier.id}`, {
                price_cents: priceCents
              })
            );
          } else {
            // Create new tier for this year
            savePromises.push(
              apiClient.post(`/api/v1/parts/${partId}/price-schedule`, {
                effective_start_date: startDate,
                effective_end_date: endDate,
                price_cents: priceCents,
                tier_name: `Year ${year}`
              })
            );
          }
        }
      }
      
      await Promise.all(savePromises);
      
      // Clear edited cells and reload
      setEditedCells({});
      setEditedPriceComponents({});
      setEditedQuotedPrices({});
      await loadData();
      
      setSavedMessage('Changes saved!');
      setTimeout(() => setSavedMessage(''), 3000);
      
      // Notify parent of price component changes
      onPriceComponentChange?.();
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setEditedCells({});
    setEditedPriceComponents({});
    setEditedQuotedPrices({});
  };

  // Format helpers
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US');
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '-';
    return `${num}%`;
  };

  const isBlankValue = (value) => value === null || value === undefined || value === '';

  const formatEditableValue = (value, format) => {
    if (isBlankValue(value)) {
      return <span className="text-gray-300">-</span>;
    }

    if (format === 'currency') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : <span className="text-gray-300">-</span>;
    }

    if (format === 'percent') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? `${parsed}%` : <span className="text-gray-300">-</span>;
    }

    return formatNumber(value);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Render editable cell - now takes column object
  const renderEditableCell = (column, field, value, format = 'number', className = '') => {
    if (!isFieldEditable(field)) {
      return (
        <div className={`px-2 py-1 text-right text-sm text-gray-700 ${className}`}>
          {formatEditableValue(getCellValue(column, field, value), format)}
        </div>
      );
    }
    const isEditing = editingCell?.column?.key === column.key && editingCell?.field === field;
    const isEdited = isCellEdited(column, field);
    const displayValue = getCellValue(column, field, value);
    
    const formattedValue = formatEditableValue(displayValue, format);
    
    if (isEditing) {
      return (
        <Input
          type="number"
          step={format === 'currency' ? '0.01' : format === 'percent' ? '0.1' : '1'}
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyDown={handleKeyDown}
          className="w-24 h-8 text-right text-sm"
          autoFocus
        />
      );
    }
    
    return (
      <div
        onClick={() => handleCellClick(column, field, displayValue)}
        className={`${readOnly ? '' : 'cursor-pointer hover:bg-blue-50'} px-2 py-1 rounded text-right text-sm transition-colors ${
          isEdited ? 'bg-blue-50 border border-blue-300' : ''
        } ${className}`}
      >
        {formattedValue}
        {isEdited && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
        )}
      </div>
    );
  };

  // Render calculated (read-only) cell
  const renderCalculatedCell = (value, format = 'number', className = '') => {
    const formattedValue = format === 'currency' 
      ? formatCurrency(value)
      : formatNumber(value);
    
    return (
      <div className={`px-2 py-1 text-right text-sm font-medium ${className}`}>
        {formattedValue}
      </div>
    );
  };

  // Render editable price component cell - still uses year for price components
  const renderPriceComponentCell = (componentType, column) => {
    const year = column.year;
    // Price components still key by year, not by column
    const pseudoColumn = { key: String(year), year };
    const isEditing = editingCell?.column?.year === year && editingCell?.field === componentType && editingCell?.isPriceComponent;
    const isEdited = isPriceComponentEdited(componentType, year);
    const value = getPriceComponentValue(componentType, year);

    if (readOnly) {
      return (
        <div className="px-2 py-1 text-right text-sm text-gray-700">
          {value === 0 ? <span className="text-gray-300">-</span> : `$${value.toFixed(2)}`}
        </div>
      );
    }

    if (isEditing) {
      return (
        <Input
          type="number"
          step="0.01"
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyDown={handleKeyDown}
          className="w-20 h-7 text-right text-sm"
          autoFocus
        />
      );
    }
    
    return (
      <div
        onClick={() => handleCellClick(pseudoColumn, componentType, value, true)}
        className={`${readOnly ? '' : 'cursor-pointer hover:bg-blue-50'} px-2 py-1 rounded text-right text-sm transition-colors ${
          isEdited ? 'bg-blue-100 border border-blue-300' : ''
        }`}
      >
        {value === 0 ? '-' : `$${value.toFixed(2)}`}
      </div>
    );
  };

  const hasUnsavedChanges = unsavedCount > 0;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (contextMode === 'part_master' || contextRequired) {
    const programCount = whereUsed.length;
    const rollupPlanned = whereUsed.reduce((sum, w) => sum + Number(w.planned_volume ?? w.volume_plan ?? 0), 0);
    const rollupRevenueCents = whereUsed.reduce((sum, w) => sum + Number(w.revenue_cents ?? 0), 0);
    const fmtUnits = (n) => Number(n || 0).toLocaleString();
    const fmtMoney = (cents) => `$${Math.round(Number(cents || 0) / 100).toLocaleString()}`;
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <CardTitle>Part Master</CardTitle>
          </div>
          <CardDescription>
            Identity and where-used. Volume, take rate, price, and revenue are planned per program.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {programCount > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                This part is used in{' '}
                <span className="font-semibold text-slate-900">
                  {programCount} {programCount === 1 ? 'program' : 'programs'}
                </span>
                . Each program owns its own market basis, take rate, content per vehicle, and S&OP.
                Select a program in <span className="font-medium text-slate-700">Program Usage</span> above to view or edit its plan.
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Across all programs (rollup)
                </div>
                <div className="mt-2 flex flex-wrap gap-x-10 gap-y-2">
                  <div>
                    <div className="text-xs text-slate-500">Planned volume</div>
                    <div className="text-lg font-semibold text-slate-900">{fmtUnits(rollupPlanned)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Revenue</div>
                    <div className="text-lg font-semibold text-slate-900">{fmtMoney(rollupRevenueCents)}</div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Read-only summary. Open a program context to plan.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              {contextLabel || 'This Part Master is not linked to a program yet. Link it to a program to plan its market basis, take rate, and S&OP.'}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Empty state when no forecasts
  if (forecasts.length === 0 && !loading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            <CardTitle>S&OP Planning Grid</CardTitle>
          </div>
          <CardDescription>
            Volume Plan = (Allocation × Take Rate × PPV) + Adjustments
          </CardDescription>
        </CardHeader>
        <CardContent className="p-12">
          <div className="text-center">
            <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Forecast Data</h3>
            <p className="text-gray-500 mb-4">
              There is no forecast data for this scenario yet. 
              Add data using the legacy forecast grid below, or switch to a different scenario.
            </p>
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      {/* Scenario Impact Banner */}
      <ScenarioImpactBanner 
        partNumber={partNumber}
        currentScenarioName={scenarioName}
        programId={programId}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaveTime={savedMessage ? Date.now() : null}
      />
      
      <Card className="overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                S&OP Planning Grid
              </CardTitle>
              {readOnly && (
                <div className="mt-2 text-sm font-medium text-amber-700">
                  {readOnlyReason || 'Context planning basis. Editing requires Program-Part planning workflow.'}
                </div>
              )}
              <CardDescription className="mt-1">
                Volume Plan = (Allocation × Take Rate × PPV) + Adjustments
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Granularity Toggle */}
              <div className="flex bg-white border rounded-lg p-0.5">
                {['annual', 'quarterly', 'monthly'].map((g) => (
                  <button
                    key={g}
                    onClick={() => setGranularity(g)}
                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                      granularity === g
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </button>
                ))}
              </div>
              
              {/* Save/Discard buttons */}
              {!readOnly && hasUnsavedChanges && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-blue-600">
                    {unsavedCount} unsaved
                  </Badge>
                  <Button variant="outline" size="sm" onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button size="sm" onClick={handleSaveAll} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save All
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {savedMessage && (
                <Badge className="bg-green-100 text-green-800">
                  <Check className="h-3 w-3 mr-1" />
                  {savedMessage}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {error && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}
          
          {/* Grid Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left p-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-30 min-w-[200px] border-r border-gray-200">
                    Metric
                  </th>
                  {timeColumns.map(col => (
                    <th 
                      key={col.key} 
                      className={`text-center p-3 font-semibold min-w-[${granularity === 'monthly' ? '100' : '140'}px] ${
                        col.isOutOfRange 
                          ? 'bg-gray-100/70 text-gray-400' 
                          : 'text-gray-700'
                      }`} 
                      colSpan={2}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center gap-1">
                            <span className={`${granularity === 'monthly' ? 'text-xs' : 'text-sm'}`}>
                              {col.label}
                            </span>
                            {col.isOutOfRange && (
                              <span className="text-[10px] text-gray-400 italic">
                                {col.year < (ihsRange.sopYear || col.year) ? 'Pre-SOP' : 'Post-EOP'}
                              </span>
                            )}
                            <div className="flex gap-1">
                              <Badge variant="outline" className={`text-xs ${col.isOutOfRange ? 'opacity-50' : ''}`}>Plan</Badge>
                              <Badge variant="outline" className={`text-xs text-gray-500 ${col.isOutOfRange ? 'opacity-50' : ''}`}>Actual</Badge>
                            </div>
                          </div>
                        </TooltipTrigger>
                        {col.isOutOfRange && (
                          <TooltipContent>
                            <p>This period is outside the IHS production range</p>
                            <p className="text-xs text-gray-400">
                              IHS Range: {ihsRange.sopYear || 'N/A'} - {ihsRange.eopYear || 'N/A'}
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              
              <tbody>
                {/* VOLUME SECTION */}
                <tr 
                  className="bg-blue-50/50 border-b cursor-pointer hover:bg-blue-100/50"
                  onClick={() => toggleSection('volume')}
                >
                  <td colSpan={timeColumns.length * 2 + 1} className="p-3">
                    <div className="flex items-center gap-2">
                      {expandedSections.volume ? (
                        <ChevronDown className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-blue-600" />
                      )}
                      <Package className="h-4 w-4 text-blue-600" />
                      <span className="font-semibold text-blue-900">VOLUME</span>
                    </div>
                  </td>
                </tr>
                
                {expandedSections.volume && (
                  <>
                    {/* IHS Market Volume (Read-Only) */}
                    <tr className="border-b hover:bg-gray-50 bg-slate-50/30">
                      <td className="p-3 text-gray-600 sticky left-0 bg-slate-50/30 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <span>IHS Market Volume</span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-slate-400">AUTO</Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Total market volume from linked IHS carline</p>
                            <p className="text-xs text-gray-400 mt-1">Read-only - auto-populated from IHS data</p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        const ihsMarketVol = data.ihs_market_volume || 0;
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 text-right text-gray-500 ${col.isOutOfRange ? 'bg-gray-50/50' : 'bg-slate-50/30'}`}>
                              {ihsMarketVol > 0 ? ihsMarketVol.toLocaleString() : <span className="text-gray-300">-</span>}
                            </td>
                            <td className={`p-2 text-gray-300 ${col.isOutOfRange ? 'bg-gray-50/50' : 'bg-slate-50/30'}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* Company Allocation (Editable) */}
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <span>Company Allocation</span>
                            {!readOnly && <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-500">EDIT</Badge>}
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Your company&apos;s expected volume allocation</p>
                            <p className="text-xs text-gray-400 mt-1">Manual entry or IHS sync</p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderEditableCell(col, 'company_allocation', getDisplayedAllocation(col, data))}
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* Take Rate (Editable) */}
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <span>Take Rate (%)</span>
                            {isFieldEditable('take_rate') && <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-500">EDIT</Badge>}
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Market share percentage</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {isFieldEditable('take_rate')
                                ? 'Editable. Take rate and Company Allocation are linked - editing one recomputes the other.'
                                : 'Read-only.'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderEditableCell(col, 'take_rate', getDisplayedTakeRate(col, data), 'percent')}
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* PPV */}
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger>Parts Per Vehicle</TooltipTrigger>
                          <TooltipContent>Number of this part per vehicle</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderEditableCell(col, 'ppv', data.ppv)}
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* Volume Adjustment */}
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger>Adjustments (+/-)</TooltipTrigger>
                          <TooltipContent>Manual volume adjustments</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderEditableCell(col, 'volume_adjustment', data.volume_adjustment)}
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* Calculated Planned Volume */}
                    <tr className="border-b-2 border-blue-200 bg-blue-50/30">
                      <td className="p-3 font-semibold text-blue-900 sticky left-0 bg-blue-50/30 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger>VOLUME PLAN</TooltipTrigger>
                          <TooltipContent>
                            <p>Volume Plan = Market Volume x Take Rate x PPV + Adjustment</p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        const plannedVol = getDisplayedPlannedVolume(col, data);
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderCalculatedCell(plannedVol, 'number', 'text-blue-900 font-bold')}
                            </td>
                            <td className={`p-2 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {renderCalculatedCell(data.volume_actual || null, 'number', 'text-gray-500')}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </>
                )}
                
                {/* PRICE SECTION - With Component Breakdown */}
                <tr 
                  className="bg-green-50/50 border-b cursor-pointer hover:bg-green-100/50"
                  onClick={() => toggleSection('price')}
                >
                  <td colSpan={timeColumns.length * 2 + 1} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {expandedSections.price ? (
                          <ChevronDown className="h-4 w-4 text-green-600" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-green-600" />
                        )}
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-green-900">PRICE</span>
                        <span className="text-xs text-green-600 ml-2">(Components to Base to LTA to Effective)</span>
                      </div>
                      {/* Revenue Driver Indicator - Smart logic */}
                      <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        <TrendingUp className="h-3 w-3" />
                        <span>Revenue Driver:</span>
                        <span className="font-medium">
                          {priceSchedule?.tiers?.length > 0 ? 'Quoted Price' : 
                           partPricing.awarded_price ? 'Awarded Price' : 
                           partPricing.target_price ? 'Customer Target Price' : 'Base Price'}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {expandedSections.price && (
                  <>
                    {/* Price Breakdown Toggle */}
                    <tr 
                      className="bg-blue-50/30 border-b cursor-pointer hover:bg-blue-100/30"
                      onClick={(e) => { e.stopPropagation(); toggleSection('priceBreakdown'); }}
                    >
                      <td colSpan={timeColumns.length * 2 + 1} className="p-2 pl-8">
                        <div className="flex items-center gap-2">
                          {expandedSections.priceBreakdown ? (
                            <ChevronDown className="h-3 w-3 text-blue-600" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-blue-600" />
                          )}
                          <Package className="h-3 w-3 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">Price Component Breakdown</span>
                          <span className="text-xs text-blue-500">(click to {expandedSections.priceBreakdown ? 'collapse' : 'expand'})</span>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Price Components - BASE COMPONENTS */}
                    {expandedSections.priceBreakdown && (
                      <>
                        {/* Base Material */}
                        {Object.entries(priceComponentTypes).filter(([_, config]) => config.isBase).map(([type, config]) => {
                          const IconComponent = config.icon;
                          return (
                            <tr key={type} className="border-b hover:bg-gray-50">
                              <td className="p-2 pl-12 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                                <div className="flex items-center gap-2">
                                  <div className={`p-1 rounded ${config.bgColor}`}>
                                    <IconComponent className={`w-3 h-3 ${config.color}`} />
                                  </div>
                                  <span className="text-sm">{config.label}</span>
                                </div>
                              </td>
                              {timeColumns.map(col => (
                                <React.Fragment key={col.key}>
                                  <td className={`p-1 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                                    {renderPriceComponentCell(type, col)}
                                  </td>
                                  <td className={`p-1 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          );
                        })}
                        
                        {/* Adjustments Header */}
                        <tr className="bg-orange-50/30">
                          <td colSpan={timeColumns.length * 2 + 1} className="p-1 pl-12">
                            <span className="text-xs font-medium text-orange-700 uppercase">Adjustments</span>
                          </td>
                        </tr>
                        
                        {/* Adjustment Components */}
                        {Object.entries(priceComponentTypes).filter(([_, config]) => !config.isBase).map(([type, config]) => {
                          const IconComponent = config.icon;
                          return (
                            <tr key={type} className="border-b hover:bg-gray-50">
                              <td className="p-2 pl-12 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                                <div className="flex items-center gap-2">
                                  <div className={`p-1 rounded ${config.bgColor}`}>
                                    <IconComponent className={`w-3 h-3 ${config.color}`} />
                                  </div>
                                  <span className="text-sm">{config.label}</span>
                                </div>
                              </td>
                              {timeColumns.map(col => (
                                <React.Fragment key={col.key}>
                                  <td className={`p-1 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                                    {renderPriceComponentCell(type, col)}
                                  </td>
                                  <td className={`p-1 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          );
                        })}
                        
                        {/* Component Total Row */}
                        <tr className="bg-slate-100 border-b">
                          <td className="p-2 pl-12 font-semibold text-slate-700 sticky left-0 bg-slate-100 z-20 border-r border-gray-200">
                            Components Total
                          </td>
                          {timeColumns.map(col => {
                            const totals = getPriceComponentTotals(col.year);
                            return (
                              <React.Fragment key={col.key}>
                                <td className={`p-2 text-right font-bold text-slate-800 ${col.isOutOfRange ? 'bg-gray-100/70' : ''}`}>
                                  ${totals.total.toFixed(2)}
                                </td>
                                <td className={`p-1 text-gray-400 ${col.isOutOfRange ? 'bg-gray-100/70' : ''}`}>-</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      </>
                    )}
                    
                    {/* Quoted Price Row - EDITABLE (creates price tiers per year) */}
                    <tr className="border-b hover:bg-blue-50/30 bg-blue-50/10">
                      <td className="p-3 text-gray-600 sticky left-0 bg-blue-50/10 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-blue-500" />
                            <span className="text-blue-700 font-medium">Quoted Price</span>
                            {!readOnly && <Badge className="text-[10px] px-1.5 py-0 ml-1 bg-blue-100 text-blue-600 border-blue-200">EDIT</Badge>}
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">Your Price Per Year</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {readOnly ? 'Managed through the Part Master price schedule.' : 'Click to edit. Creates yearly price tiers for the contract.'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => (
                        <React.Fragment key={col.key}>
                          <td className={`p-2 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                            {renderQuotedPriceCell(col)}
                          </td>
                          <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                        </React.Fragment>
                      ))}
                    </tr>
                    
                    {/* Customer Target Price Row (Reference) */}
                    {partPricing.target_price && (
                      <tr className="border-b hover:bg-orange-50/30 bg-orange-50/10">
                        <td className="p-3 text-gray-600 sticky left-0 bg-orange-50/10 z-20 border-r border-gray-200">
                          <Tooltip>
                            <TooltipTrigger className="flex items-center gap-1">
                              <Tag className="w-4 h-4 text-orange-500" />
                              <span className="text-orange-700 font-medium">Customer Target Price</span>
                              <Badge className="text-[10px] px-1.5 py-0 ml-1 bg-orange-100 text-orange-600 border-orange-200">
                                REFERENCE
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">Customer Target Price</p>
                              <p className="text-xs text-gray-400 mt-1">Reference target only. Does not drive revenue.</p>
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        {timeColumns.map(col => (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 text-right ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              <span className="text-orange-600 font-medium">${partPricing.target_price.toFixed(2)}</span>
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        ))}
                      </tr>
                    )}
                    
                    {/* Awarded Price Row - Reference from Part */}
                    <tr className="border-b hover:bg-green-50/30 bg-green-50/10">
                      <td className="p-3 text-gray-600 sticky left-0 bg-green-50/10 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-green-500" />
                            <span className="text-green-700">Awarded Price</span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1 text-green-400">REF</Badge>
                          </TooltipTrigger>
                          <TooltipContent>Final contracted price after award</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => (
                        <React.Fragment key={col.key}>
                          <td className={`p-2 text-right ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                            {partPricing.awarded_price ? (
                              <span className="text-green-600 font-medium">${partPricing.awarded_price.toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-300 text-xs">Pending</span>
                            )}
                          </td>
                          <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                        </React.Fragment>
                      ))}
                    </tr>
                    
                    {/* Base Price (Summary Row - always visible) */}
                    <tr className="border-b hover:bg-gray-50 bg-green-50/20">
                      <td className="p-3 text-gray-700 font-medium sticky left-0 bg-green-50/20 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <Package className="w-4 h-4 text-green-600" />
                            Base Price
                          </TooltipTrigger>
                          <TooltipContent>Sum of base components (Material + Labor + Overhead). Enter components above to calculate.</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        // Base Price = ONLY from components (Material + Labor + Overhead)
                        const componentTotal = getPriceComponentTotals(col.year);
                        const basePrice = componentTotal.base; // Always use component total, no fallback
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              <div className="text-right font-semibold text-green-800">
                                {basePrice > 0 ? `$${basePrice.toFixed(2)}` : <span className="text-gray-400 font-normal">$0.00</span>}
                              </div>
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* LTA Effect Row - Shows both % and $ impact */}
                    <tr className="border-b hover:bg-gray-50 bg-red-50/30">
                      <td className="p-3 text-gray-600 sticky left-0 bg-red-50/30 z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1">
                            <span>LTA Effect</span>
                          </TooltipTrigger>
                          <TooltipContent>Long-Term Agreement price reduction (% and $ value)</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const data = getColumnData(col);
                        const componentTotal = getPriceComponentTotals(col.year);
                        const basePrice = componentTotal.base > 0 ? componentTotal.base : getCellValue(col, 'base_price', data.base_price);
                        const ltaPercent = getCellValue(col, 'lta_percent', data.lta_percent);
                        const ltaDollarReduction = basePrice * (ltaPercent / 100);
                        const hasLTA = ltaPercent > 0;
                        
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              <div className="flex flex-col items-center">
                                {/* Editable LTA % */}
                                <div className="text-xs text-gray-500 mb-0.5">
                                  {renderEditableCell(col, 'lta_percent', ltaPercent, 'percent')}
                                </div>
                                {/* Calculated $ reduction */}
                                {hasLTA && (
                                  <span className="text-xs text-red-600 font-medium">
                                    -${ltaDollarReduction.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    
                    {/* Price Adjustments (from components if available) */}
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 sticky left-0 bg-white z-20 border-r border-gray-200">
                        <Tooltip>
                          <TooltipTrigger>+ Adjustments</TooltipTrigger>
                          <TooltipContent>Surcharges, FX impact, logistics, etc.</TooltipContent>
                        </Tooltip>
                      </td>
                      {timeColumns.map(col => {
                        const componentTotal = getPriceComponentTotals(col.year);
                        const adjustments = componentTotal.adjustments;
                        return (
                          <React.Fragment key={col.key}>
                            <td className={`p-2 relative text-right ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                              {adjustments === 0 ? (
                                <span className="text-gray-400">-</span>
                              ) : (
                                <span className={adjustments > 0 ? 'text-orange-600' : 'text-green-600'}>
                                  {adjustments > 0 ? '+' : ''}${adjustments.toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td className={`p-2 text-gray-400 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>-</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </>
                )}
                
                {/* REVENUE SECTION */}
                <tr 
                  className="bg-purple-50/50 border-b cursor-pointer hover:bg-purple-100/50"
                  onClick={() => toggleSection('revenue')}
                >
                  <td colSpan={timeColumns.length * 2 + 1} className="p-3">
                    <div className="flex items-center gap-2">
                      {expandedSections.revenue ? (
                        <ChevronDown className="h-4 w-4 text-purple-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-purple-600" />
                      )}
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                      <span className="font-semibold text-purple-900">REVENUE</span>
                    </div>
                  </td>
                </tr>
                
                {expandedSections.revenue && (
                  <tr className="border-b-2 border-purple-200 bg-purple-50/30">
                    <td className="p-3 font-semibold text-purple-900 sticky left-0 bg-purple-50/30 z-20 border-r border-gray-200">
                      TOTAL REVENUE
                    </td>
                    {timeColumns.map(col => {
                      const data = getColumnData(col);
                      
                      // Read mode shows server-canonical revenue (volume_plan x effective price
                      // from the commercial calculator). Edit mode recomputes for instant feedback.
                      const plannedVol = getDisplayedPlannedVolume(col, data);

                      // Calculate effective price from components
                      const componentTotal = getPriceComponentTotals(col.year);
                      const basePrice = componentTotal.base > 0 ? componentTotal.base : getCellValue(col, 'base_price', data.base_price);
                      const ltaPercent = getCellValue(col, 'lta_percent', data.lta_percent);
                      const adjustments = componentTotal.adjustments;
                      const ltaReduction = basePrice * (ltaPercent / 100);
                      const effectivePrice = basePrice - ltaReduction + adjustments;

                      const revenue = readOnly
                        ? (data.revenue_cents || 0) / 100
                        : plannedVol * effectivePrice;
                      // Only show actual revenue when real actuals exist - never a fabricated $0.
                      const actualRevenue = (data.volume_actual && data.price_actual)
                        ? data.volume_actual * data.price_actual
                        : null;
                      
                      return (
                        <React.Fragment key={col.key}>
                          <td className={`p-2 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                            {renderCalculatedCell(revenue, 'currency', 'text-purple-900 font-bold text-base')}
                          </td>
                          <td className={`p-2 ${col.isOutOfRange ? 'bg-gray-50/50' : ''}`}>
                            {renderCalculatedCell(actualRevenue, 'currency', 'text-gray-500')}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Legend */}
          <div className="p-4 bg-gray-50 border-t flex flex-wrap items-center gap-6 text-xs text-gray-600">
            {!readOnly && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border border-blue-300 bg-blue-50" />
                  <span>Edited (unsaved)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-100 border" />
                  <span>Click to edit</span>
                </div>
              </>
            )}
            {readOnly && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border border-amber-300 bg-amber-50" />
                <span>Read-only context planning basis</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-bold text-blue-900">Bold</span>
              <span>= Calculated (read-only)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-100/70 border border-gray-200" />
              <span>Outside IHS range</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default UnifiedPlanningGrid;
